import { match } from '@formatjs/intl-localematcher'
import Negotiator, { Headers as NegotiatorHeaders } from 'negotiator'
import { getLocaleFromLocaleString, LocaleStrings } from './localeConstants'

/**
 * Returns the locale from given HTTP headers, or the given fallback locale if a locale can't be determined.
 *
 * @param {Headers} headers - The HTTP headers containing 'accept-language'.
 * @param {LocaleStrings} [defaultLocaleString=LocaleStrings.NL] - The default locale string to use if no matching locale is found.
 * @param {LocaleStrings[]} [localeStrings=Object.values(LocaleStrings)] - An array of supported locale strings.
 * @returns {string} The best matching locale string.
 */
export const getLocaleFromHeaders = (
    headers: Headers,
    defaultLocaleString: LocaleStrings = LocaleStrings.NL,
    localeStrings: LocaleStrings[] = Object.values(LocaleStrings),
): string => {
    const headersToNegotiate: NegotiatorHeaders = { 'accept-language': headers.get('accept-language') ?? undefined }
    const languages = filterUnsupportedLanguages(
        new Negotiator({ headers: headersToNegotiate }).languages(),
        defaultLocaleString,
    )
    const locale = match(languages, localeStrings, defaultLocaleString)
    return String(getLocaleFromLocaleString(locale as LocaleStrings))
}

/**
 * Filters out unsupported languages from the given list of languages.
 *
 * @param {string[]} languages - The languages to be filtered.
 * @param {LocaleStrings} defaultLocaleString - The default locale string to use if no supported languages are found.
 * @returns {string[]} The filtered list of supported languages.
 */
const filterUnsupportedLanguages = (languages: string[], defaultLocaleString: LocaleStrings): string[] => {
    const UNSUPPORTED_LANGUAGES = ['*']
    let filteredLanguages = languages.filter((language) => !UNSUPPORTED_LANGUAGES.includes(language))
    if (filteredLanguages.length === 0) filteredLanguages = [defaultLocaleString]
    return filteredLanguages
}
